import { IProperty } from '../../../data/entities/property';
import { IPropertyScoreAttribute, IPropertyScoreSummary } from '../../../data/entities/property-score';

export const getContextualHeading = (properties: IProperty[]) => {
	if (properties?.length > 1) {
		return 'Your property scores';
	}
	return properties[0]?.internal_name || '';
};

const hasUnpublishedChanges = (latestVersion: number, publishedVersion?: number | null) => publishedVersion ? latestVersion > publishedVersion : true;

export const propertyNeedsToPublish = (property: IProperty) =>
	[
		property,
		...(property.bathrooms ?? []),
		...(property.beds ?? []),
		...(property.amenities ?? []),
		...(property.restrictions ?? [])
	].some(
		(t) => hasUnpublishedChanges(t.latest_version, t.published_version)
	);

const formatter = new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'numeric', day: 'numeric', minute: '2-digit', hour: '2-digit' });

export const formatDateTimeString = (dt: Date) => {
	const date = new Date(dt);
	return formatter.format(date);
};

export interface IPropertyWithScore extends IProperty {
	totalScore: number | undefined;
}
export const appendScoreToProperty = (
	properties: IProperty[],
	scores: IPropertyScoreSummary[],
): IPropertyWithScore[] => properties.map(
	(p) => {
		const currentPropertyScore = scores.find((s) => s.property_id === p.id);
		return {
			...p,
			internal_name: `${p.internal_name} (${currentPropertyScore?.total_score}/100)`,
			totalScore: currentPropertyScore?.total_score
		};
	}
);

export const filterRecommendationToDo = (r: IPropertyScoreAttribute) => r.points < 5 && !r.snoozed_until;
export const filterRecommendationDone = (r: IPropertyScoreAttribute) => r.points === 5;
export const filterRecommendationSnoozed = (r: IPropertyScoreAttribute) => r.points < 5 && !!r.snoozed_until;

const sortRecommendationsByImpact = (current: IPropertyScoreAttributeWithIncrease, next: IPropertyScoreAttributeWithIncrease) =>
	next.potentialScoreIncrease - current.potentialScoreIncrease;

const sortRecommendationsByDateCompleted = (current: IPropertyScoreAttributeWithIncrease, next: IPropertyScoreAttributeWithIncrease) => {
	const d1 = new Date(next.updated_at);
	const d2 = new Date(current.updated_at);
	return +d1 - +d2;
};
export interface IPropertyScoreAttributeWithIncrease extends IPropertyScoreAttribute {
	potentialScoreIncrease: number;
}

export const appendPotentialRecommendationScoreIncrease = (attributes: IPropertyScoreAttribute[]): IPropertyScoreAttributeWithIncrease[] => {
	const totalScore = attributes.reduce((total, current) => total += current.weight * 5, 0);
	return attributes.map((attr) => {
		const calculation = (((5 - attr.points) * attr.weight) / totalScore) * 100;
		const potentialScoreIncrease = calculation === 0 ? calculation : Math.max(Math.round(calculation), 1);
		return { ...attr, potentialScoreIncrease };
	});
};

export const categoriseRecommendations = (
	attributes: IPropertyScoreAttributeWithIncrease[]
): { [key: string]: { recommendations: IPropertyScoreAttributeWithIncrease[], count: number, unread: boolean, text: string } } => {
	const filteredTodo = attributes.filter(filterRecommendationToDo).sort(sortRecommendationsByImpact);
	const filteredDone = attributes.filter(filterRecommendationDone).sort(sortRecommendationsByDateCompleted);
	const filteredSnoozed = attributes.filter(filterRecommendationSnoozed).sort(sortRecommendationsByImpact);

	return {
		todo: {
			recommendations: filteredTodo,
			count: filteredTodo.length,
			unread: false,
			text: 'To do'
		},
		done: {
			recommendations: filteredDone,
			count: filteredDone.length,
			unread: filteredDone.some((attr) => !attr.viewed_at),
			text: 'Done'
		},
		snoozed: {
			recommendations: filteredSnoozed,
			count: filteredSnoozed.length,
			unread: false,
			text: 'Snoozed'
		}
	};
};

export const getLowestScoringProperty = (
	customerProperties: IProperty[], propertyScores: IPropertyScoreSummary[]):
	IProperty & { totalScore: number } & { calculatingState: boolean } | null => {

	const activeAndPublishedProperties = customerProperties.filter((p: IProperty) => p.is_active && !!p.published_at);

	if (activeAndPublishedProperties?.length > 0 && propertyScores?.length > 0) {
		const propertyScoresForPublishedProperties = propertyScores.filter((p) => activeAndPublishedProperties.find((cp) => cp.id === p.property_id));
		const lowestScoringProperty = propertyScoresForPublishedProperties.sort((current, next) => current.total_score - next.total_score)[0];
		const scoreCalculatingState = checkPropertyScoreCalculatingState(lowestScoringProperty);
		return {
			...activeAndPublishedProperties.filter((cp) => cp.id === lowestScoringProperty.property_id)[0],
			totalScore: lowestScoringProperty.total_score, calculatingState: scoreCalculatingState
		};
	}
	return null;
};

export const checkPropertyScoreCalculatingState = (propertyScore: IPropertyScoreSummary) => {
	if (propertyScore.calculating) {
		return true;
	}
	return false;
};
